import React, { useState, useCallback, useEffect, Fragment } from "react";
import { useDispatch } from "react-redux";
import screens, { prevScreenNum, summaryScreenName, nextScreenNum } from "../screens/screens";
import { defaultFirebaseClient } from "../clients/FirebaseClient";
import {
    ChangeNames,
    unwrap,
    OperationState,
    changesToFormModel,
    FormModel,
    Configuration,
} from "../clients/FirebaseClient/types";
import { selectChange } from "../clients/ConfigurationClient/tools";
import { Grid, useTheme, useMediaQuery, CircularProgress, makeStyles } from "@material-ui/core";
import { Formik, FormikProps, useFormikContext } from "formik";
import { ScreenActions, RootDispatch, useTypedSelector } from "../redux";
import { MultiChangeRenderer } from "./ChangeRenderer";
import { isEqual } from "lodash";
import { useStyles } from "../useStyles";
import { Button } from "../components/core/Button";
import { Menu } from "./ChangeMenu";
import { Summary } from "../screens/summary";
import { ConfigurationActions } from "../redux/slices/configurationSlice";
import { ErrorBanner } from "./core/ErrorBanner";
import { Colors } from "../styles";
import { BuildingAddressHeader } from "./core/BuildingAddressHeader";
import { useNavigate } from "react-router-dom";
interface Props {
    configuration: Configuration;
    uid: string;
}

const useStyles2 = makeStyles(() => ({
    root: {
        color: Colors.mellowYellow,
    },
    buttonImportant: {
        backgroundColor: Colors.pulsingPurple,
        "&:hover": {
            backgroundColor: Colors.velvet,
        },
        "&:active": {
            backgroundColor: Colors.deepViolett,
        },
    },
}));

export const ScreenLoader: React.FC = () => {
    const classes = useStyles2();
    const { currentConfiguration, uid } = useTypedSelector((state) => ({
        currentConfiguration:
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            state.configuration.configurations[state.configuration.currentConfigurationId!],
        uid: state.auth.uid,
    }));

    if (currentConfiguration && uid) {
        if (currentConfiguration.lockedAt) {
            return <Summary configuration={currentConfiguration} />;
        }
        const screenFormModel = changesToFormModel(currentConfiguration.state);
        return (
            <Formik
                initialValues={screenFormModel}
                onSubmit={() =>
                    console.warn(
                        "formik.onSubmit triggered without being set to an actual submit function",
                    )
                }
                enableReinitialize
            >
                <ScreenRenderer configuration={currentConfiguration} uid={uid} />
            </Formik>
        );
    } else {
        return (
            <div
                style={{
                    position: "absolute",
                    backgroundColor: "#ffffff99",
                    display: "flex",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    justifyContent: "center",
                    alignItems: "center",
                    justifyItems: "center",
                    flexDirection: "column",
                }}
            >
                <CircularProgress variant="indeterminate" className={classes.root} />
                <p style={{ width: "auto" }}>Formular wird vorbereitet</p>
            </div>
        );
    }
};

export const ScreenRenderer: React.FC<Props> = (props: Props) => {
    const classes = useStyles();

    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [scrollToChangeName, setScrollToChangeName] = useState<string | undefined>(undefined);

    const dispatch: RootDispatch = useDispatch();
    const navigate = useNavigate();
    const formik = useFormikContext<FormModel<ChangeNames>>();
    const shouldShowMenu = useTypedSelector((state) => state.screen.menuVisible);
    const screenNum = useTypedSelector((state) => state.screen.screenNum);

    useEffect(() => {
        if (!scrollToChangeName) {
            window.scrollTo(0, 0);
        } else {
            setScrollToChangeName(undefined);
        }
    }, [screenNum, scrollToChangeName]);

    const submitWithOperation = useCallback(
        async function (formik: FormikProps<FormModel<ChangeNames>>, submitDefaults = true) {
            const changeSet = (Object.keys(formik.values) as Array<ChangeNames>)
                .filter((changeName) => screens[screenNum].changes.indexOf(changeName) >= 0)
                .filter((changeName) => selectChange(changeName)?.relevant)
                .filter((changeName) => {
                    const state = selectChange(changeName);
                    return (
                        (submitDefaults && state?.default) ||
                        !isEqual(
                            formik.values[changeName],
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            selectChange(changeName)?.state!,
                        )
                    );
                })
                .map((changeName) => ({
                    [changeName]: unwrap(formik.values[changeName]),
                }))
                .reduce((a, b) => ({ ...a, ...b }), {});

            console.log("detected changes:", changeSet);
            dispatch(ScreenActions.setActivity());
            try {
                const operationState =
                    Object.keys(changeSet).length === 0
                        ? {
                              status: OperationState.SUCCEEDED,
                          }
                        : await defaultFirebaseClient.doWithOperation(
                              props.uid,
                              async (): Promise<string | void> => {
                                  if (!props.configuration.id) return;
                                  console.log(`updating configuration ${props.configuration.id}`);
                                  await dispatch(
                                      ConfigurationActions.change({
                                          confId: props.configuration.id,
                                          conf: changeSet,
                                      }),
                                  );
                              },
                          );

                return operationState;
            } finally {
                dispatch(ScreenActions.finishActivity());
            }
        },
        [screenNum, props, dispatch],
    );

    const navigateTo = useCallback(
        async (formik: FormikProps<FormModel<ChangeNames>>, direction: "forward" | "backward") => {
            const willCreateConfigId = props.configuration.id === "new";
            const operationState = await submitWithOperation(formik, direction === "forward");
            const updateScreenNum = (): void => {
                const newScreenNum =
                    direction === "forward" ? nextScreenNum(screenNum) : prevScreenNum(screenNum);
                if (typeof newScreenNum !== "undefined") {
                    dispatch(ScreenActions.setScreenNum(newScreenNum));
                }
            };
            if (willCreateConfigId) {
                if (
                    (operationState.status === OperationState.SUCCEEDED ||
                        operationState.status === OperationState.VALIDATION_FAILED) &&
                    operationState.result
                ) {
                    dispatch(ConfigurationActions.load(operationState.result));
                    if (operationState.status === OperationState.SUCCEEDED) updateScreenNum();
                    navigate(`/configuration/${operationState.result}`);
                }
            } else {
                if (operationState.status === OperationState.SUCCEEDED) updateScreenNum();
            }
        },
        [props.configuration.id, submitWithOperation, screenNum, dispatch, navigate],
    );

    const onOrder = useCallback(async () => {
        const {
            configuration: { id, state },
        } = props;
        console.log("submittting changes");
        const operationState = await submitWithOperation(formik, true);
        if (operationState.status !== OperationState.SUCCEEDED) {
            return;
        }
        console.log("successfully submitted");
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const hasError = (o: any): boolean =>
            Object.keys(o).some(function (k) {
                if (k === "error") {
                    return !!o[k];
                }
                if (o[k] && typeof o[k] === "object") {
                    return hasError(o[k]);
                }
                return false;
            });
        const filteredState = Object.entries(state).filter(
            ([, state]) => state?.relevant && state?.state,
        );
        const someError = filteredState.some(([, state]) => hasError(state));
        if (someError) {
            alert("Validierungsfehler vorhanden!");
        } else {
            try {
                if (!id) {
                    console.error("attempting to order a configuration that is not yet created!");
                    return;
                }
                await dispatch(ConfigurationActions.order(id));
                navigate(`/confirmation/${id}`);
            } catch (e) {
                console.error(e);
            }
        }
    }, [dispatch, formik, navigate, props, submitWithOperation]);

    const screen = screens[screenNum];
    const hasPrevScreen = prevScreenNum(screenNum) !== undefined;

    const buildMenu = (formik: FormikProps<FormModel<ChangeNames>>): JSX.Element => (
        <Menu
            navigate={async (screenId) => {
                const operationState = await submitWithOperation(formik, false);
                if (operationState.status === OperationState.SUCCEEDED) {
                    const screen = screens.findIndex((screen) => screen.id === screenId);
                    if (screen < 0) {
                        alert("unknown screen");
                    } else {
                        dispatch(ScreenActions.setScreenNum(screen));
                        dispatch(ScreenActions.setMenuVisible(false));
                    }
                }
            }}
            current={screens[screenNum].id}
        />
    );

    const relevantChangesContainsError = useCallback(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const hasError = (o: any): boolean =>
            Object.keys(o).some(function (k) {
                if (k === "error") {
                    return !!o[k];
                }
                if (o[k] && typeof o[k] === "object") {
                    return hasError(o[k]);
                }
                return false;
            });

        return screen.changes
            .filter((changeName) => props.configuration.state[changeName]?.relevant)
            .some((changeName) => {
                return hasError(props.configuration.state[changeName]);
            });
    }, [props.configuration, screen.changes]);

    return (
        <Fragment>
            <BuildingAddressHeader isDeepProductCheck={true} isChangeAddressButtonVisible={false} />
            <Grid
                className={classes.screenBody}
                spacing={2}
                container
                data-testid="configuration-flow-testId"
            >
                {!smallScreen && (
                    <Grid sm={4} item style={{ paddingLeft: 24, marginTop: 20, marginBottom: 20 }}>
                        {buildMenu(formik)}
                    </Grid>
                )}
                {shouldShowMenu && smallScreen ? (
                    <div
                        style={{
                            paddingLeft: 24,
                            marginTop: 20,
                            marginBottom: 20,
                            paddingRight: 24,
                        }}
                    >
                        {buildMenu(formik)}
                    </div>
                ) : (
                    <Grid sm={12} md={8} item style={{ paddingTop: 24 }}>
                        {relevantChangesContainsError() && <ErrorBanner />}

                        {screen.RenderComponent ? (
                            <screen.RenderComponent
                                configuration={props.configuration}
                                options={{
                                    navigate: async (screenId, scrollToChangeName) => {
                                        const operationState = await submitWithOperation(
                                            formik,
                                            false,
                                        );
                                        if (operationState.status === OperationState.SUCCEEDED) {
                                            const screen = screens.findIndex(
                                                (screen) => screen.id === screenId,
                                            );
                                            if (screen < 0) {
                                                alert("unknown screen");
                                            } else {
                                                if (scrollToChangeName)
                                                    setScrollToChangeName(scrollToChangeName);
                                                dispatch(ScreenActions.setScreenNum(screen));
                                                dispatch(ScreenActions.setMenuVisible(false));
                                            }
                                        }
                                    },
                                    scrollToChangeName,
                                }}
                            />
                        ) : (
                            <Fragment>
                                <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                                    {screen.name}
                                </h2>
                                {screen.topArea && screen.topArea}
                                {props.configuration.error ? (
                                    <div className={classes.error}>{props.configuration.error}</div>
                                ) : (
                                    <div />
                                )}
                                <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                                    <MultiChangeRenderer
                                        changes={screen.changes}
                                        configuration={props.configuration}
                                        scrollToChangeName={scrollToChangeName}
                                    />
                                </div>
                            </Fragment>
                        )}
                        <div className={classes.navButtonBar}>
                            {hasPrevScreen ? (
                                <Button
                                    mode="secondary"
                                    onClick={() => navigateTo(formik, "backward")}
                                    style={{ marginLeft: 20, marginBottom: 16 }}
                                >
                                    Zurück
                                </Button>
                            ) : (
                                <div />
                            )}
                            &nbsp;
                            {screen.id !== summaryScreenName ? (
                                <Button
                                    mode="primary"
                                    onClick={() => navigateTo(formik, "forward")}
                                    style={{ marginRight: 20, marginBottom: 16 }}
                                >
                                    Weiter
                                </Button>
                            ) : (
                                <Button
                                    mode="important"
                                    variant="contained"
                                    onClick={onOrder}
                                    disabled={!props.configuration.complete}
                                    style={{ marginRight: 20, marginBottom: 16 }}
                                >
                                    Antrag absenden
                                </Button>
                            )}
                        </div>
                    </Grid>
                )}
            </Grid>
        </Fragment>
    );
};
